import { useMemo, type ReactElement, type ReactNode } from "react";
import { MsalProvider } from "@azure/msal-react";
import type { PublicClientApplication } from "@azure/msal-browser";
import type { AppConfig } from "./authConfig";
import { AuthConfigContext } from "./authContext";

export interface AuthProviderProps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
    readonly children: ReactNode;
}

/**
 * Synchronous provider that wires a pre-initialized MSAL instance and
 * validated runtime config into the React tree. MSAL initialization
 * (including `handleRedirectPromise`) happens in `main.tsx` via top-level
 * await — before React mounts — so this component never needs async boot
 * logic or loading/error states.
 */
export function AuthProvider({ instance, config, children }: AuthProviderProps): ReactElement {
    const configValue = useMemo(() => config, [config]);
    return (
        <MsalProvider instance={instance}>
            <AuthConfigContext.Provider value={configValue}>{children}</AuthConfigContext.Provider>
        </MsalProvider>
    );
}
